import { useContext, useMemo, useRef, useState } from "react"
import { useEffect } from "react"
import { useCallback } from "react"
import { DateTime } from "luxon"
import _ from "lodash"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//SOAR components
import { LoaderMedium } from "../../../components/Loaders/index"

//primeReact
import { Dialog } from "primereact/dialog"
import { FileUpload } from "primereact/fileupload"
import { Tooltip } from "primereact/tooltip"

//Formik
import { Form, Formik, useField } from "formik"
import * as Yup from "yup"

//Formik helpers
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikMultiSelectInput from "../../../helpers/formikInputs/FormikMultiSelectInput"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikNumberInput from "../../../helpers/formikInputs/FormikNumberInput"

//Helpers
import { LogError } from "../../../helpers/logger"

//Housing components
import { getHouseImageUrl } from "../shared"

//Housing API and components
import {
  housingApiCreateHousePost,
  housingApiGetHouseGet,
  housingApiUpdateHousePost,
  housingApiUploadHouseImagePost,
} from "../housingApi"
import myStyle from "../../../styles/housing/HousingManagementForm.module.css"
import Room from "./Room"

import HouseAddressSearch from "./HouseAddressSearch"
import HousePrograms from "./HousePrograms"

//GraphQL
import { GET_PROGRAM } from "../queries"
import { useApolloClient } from "@apollo/client"

export default function HouseForm({
  showForm,
  onHideForm,
  onHouseCreated,
  houseId,
  organizations,
  houses,
}) {
  const userCtx = useContext(UserContext)
  const noRentDueValue = "No rent due"

  const newHouse = useCallback(() => {
    return {
      house: {
        name: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        image: "",
        rentFrequency: "",
        rentFrequencyDetail: undefined,
        rentValue: 0,
        rentGraceDays: 0,
        enterpriseId: userCtx.session.user.enterprise_id,
        organizationIds: [],
      },
      rooms: [
        {
          id: -1,
          houseId: undefined,
          name: `room #1`,
          removalDate: null,
          isNew: true,
        },
      ],
      beds: [
        {
          id: -1,
          roomId: -1,
          name: "Bed #1",
          removalDate: null,
          isNew: true,
          isAllocated: false,
        },
      ],
      organizations: [],
      programs: [],
      userId: userCtx.session.user.staff_id,
    }
  }, [userCtx.session.user.enterprise_id, userCtx.session.user.staff_id])

  const client = useApolloClient()
  const [isLoading, setIsLoading] = useState(false)
  const [house, setHouse] = useState(newHouse())
  const [refresh, setRefresh] = useState(false)
  const [frequencyOptions, setFrequencyOptions] = useState([])
  const [newHouseId, setNewHouseId] = useState(undefined)
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true)

  const fileUploadRef = useRef(null)
  let occupiedBeds = house.beds.filter((x) => !!x.isAllocated).length

  const houseImageUrl = useMemo(
    () => getHouseImageUrl(house.houseImageUrl),
    [house.houseImageUrl],
  )

  const weeklyOptions = useCallback(() => {
    return [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ]
  }, [])

  const monthlyOptions = useCallback(() => {
    return Array.from({ length: 31 }, (_, i) => (i + 1).toString())
  }, [])

  const getHouse = useCallback(async () => {
    try {
      if (!!houseId || newHouseId) {
        setIsLoading(true)
        let result = await housingApiGetHouseGet(
          userCtx.tokens.getHousingToken(),
          !!houseId ? houseId : newHouseId,
        )

        if (!!result) {
          result.house.organizationIds = result.organizations.map(
            (organization) => organization.organizationId,
          )

          result.beds.forEach((bed) => {
            bed.isAllocated = result.bedOccupations.some(
              (occupation) => occupation.bedId === bed.id,
            )
          })

          //Copy the programs array and create a UI compatible version
          result.originalPrograms = result.programs
          result.programs = await Promise.all(
            result.programs.map(async (program) => {
              /*               const soarProgram = await getProgram({
                variables: {
                  staff_id: staff_id,
                  organization_id: program.organizationId,
                  program_id: program.programId,
                },
              }) */

              const soarProgram = await client.query({
                query: GET_PROGRAM,
                variables: {
                  staff_id: userCtx.session.user.staff_id,
                  organization_id: program.organizationId,
                  program_id: program.programId,
                },
              })

              return {
                organization_name:
                  soarProgram?.data?.organization?.organization_name,
                program_id: program.programId,
                program_id_name: soarProgram?.data?.program?.program_id_name,
                organization_id: program.organizationId,
              }
            }),
          )

          result.userId = userCtx.session.user.staff_id

          setHouse(result)

          if (result.house.rentFrequency === "Weekly") {
            setFrequencyOptions(weeklyOptions())
          } else if (result.house.rentFrequency === "Monthly") {
            setFrequencyOptions(monthlyOptions())
          }
        } else {
          throw new Error("House not found")
        }

        setIsLoading(false)
      } else {
        setHouse(newHouse())
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to retrieve House profile", error)
      userCtx.setNotificationError("Unable to retrieve house profile")
      setHouse(newHouse)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    houseId,
    newHouseId,
    newHouse,
    userCtx.setNotificationError,
    userCtx.session.user.staff_id,
  ])

  useEffect(() => {
    getHouse()
  }, [getHouse])

  Yup.addMethod(Yup.array, "uniqueRoomName", function (message) {
    return this.test("uniqueRoomName", "", function (list) {
      const errors = []
      const propertyPath = "name"

      list.forEach((item, index) => {
        const propertyValue = _.get(item, propertyPath)

        if (
          propertyValue &&
          _.filter(list, (item) => {
            return item[propertyPath] === propertyValue && !item.removalDate
          }).length > 1
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            }),
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  })

  Yup.addMethod(Yup.array, "uniqueBedName", function (message) {
    return this.test("uniqueBedName", "", function (list) {
      const errors = []
      const propertyPath = "name"
      const roomIdPath = "roomId"

      list.forEach((item, index) => {
        const propertyValue = _.get(item, propertyPath)
        const roomIdValue = _.get(item, roomIdPath)

        if (
          propertyValue &&
          _.filter(list, (item) => {
            return (
              item[propertyPath] === propertyValue &&
              !item.removalDate &&
              item[roomIdPath] === roomIdValue
            )
          }).length > 1
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            }),
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  })

  const validationSchema = Yup.object().shape({
    house: Yup.object().shape({
      id: Yup.string(),
      name: Yup.string()
        .required("Please enter a name")
        .test(
          "uniqueNameTest",
          "The house name is not unique, please enter a unique name",
          (value) => {
            return !houses.some(
              (house) =>
                house.name === value && house.id !== (houseId || newHouseId),
            )
          },
        ),
    }),
    rooms: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          name: Yup.string().required("Please enter a name"),
        }),
      )
      .uniqueRoomName("Duplicate name"),
    beds: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Please enter a name"),
        }),
      )
      .uniqueBedName("Duplicate name"),
  })

  async function submitHouseProfile(values, setErrors) {
    try {
      let result = undefined

      if (
        fileUploadRef.current.files?.length > 0 &&
        fileUploadRef.current.files[0].size > 1048576
      ) {
        userCtx.setNotificationError(
          "Unable to save the house profile: The house image is larger than 1Mb, please select a smaller image",
        )
        return false
      }

      if (!!values.house.id) {
        const requestModel = JSON.parse(JSON.stringify(values))

        //Check for any removed organizations
        requestModel.organizations.forEach((org) => {
          if (
            !requestModel.house.organizationIds.includes(org.organizationId)
          ) {
            org.removalDate = DateTime.now().toUTC().toISO()
          }
        })

        //Check for any new organizations
        requestModel.newOrganizations = []
        requestModel.house.organizationIds.forEach((organizationId) => {
          if (
            !requestModel.organizations.some(
              (organization) => organization.organizationId === organizationId,
            )
          ) {
            requestModel.newOrganizations.push(organizationId)
          }
        })

        //Check for any new programs
        requestModel.newPrograms = []
        requestModel.programs.forEach((newProgram) => {
          if (
            !requestModel.originalPrograms.some(
              (originalProgram) =>
                originalProgram.programId === newProgram.program_id &&
                originalProgram.organizationId === newProgram.organization_id,
            )
          ) {
            requestModel.newPrograms.push({
              programId: newProgram.program_id,
              organizationID: newProgram.organization_id,
            })
          }
        })

        //Check for any removed programs
        requestModel.originalPrograms.forEach((program) => {
          if (
            !requestModel.programs.some(
              (newProgram) =>
                newProgram.program_id === program.programId &&
                newProgram.organizationId === program.organization_id,
            )
          ) {
            program.removalDate = DateTime.now().toUTC().toISO()
          }
        })
        requestModel.programs = requestModel.originalPrograms

        //Set rooms to edit
        const newRooms = requestModel.rooms.filter(
          (room) => !!room.isNew && !room.removalDate,
        )
        //Note: We only care about new beds that have not been removed.
        let newBeds = requestModel.beds.filter(
          (bed) => !!bed.isNew && !bed.removalDate,
        )
        let ignoreNewBedIds = []

        requestModel.rooms = requestModel.rooms.filter((room) => !room.isNew)
        requestModel.newRooms = newRooms.map((room) => {
          const newRoomBeds = newBeds.filter((bed) => bed.roomId === room.id)
          const newRoomBedsToAdd = newRoomBeds.filter((bed) => !bed.removalDate)

          ignoreNewBedIds.push(...newRoomBeds.map((bed) => bed.id))

          return {
            name: room.name,
            bedNames: newRoomBedsToAdd.map((bed) => {
              return bed.name
            }),
          }
        })

        //Set beds to edit
        requestModel.beds = requestModel.beds.filter((bed) => !bed.isNew)
        requestModel.newBeds = newBeds
          .filter((bed) => !ignoreNewBedIds.includes(bed.id))
          .map((bed) => {
            return { roomId: bed.roomId, name: bed.name }
          })

        //Submit edit
        result = await housingApiUpdateHousePost(
          userCtx.tokens.getHousingToken(),
          requestModel,
        )
      } else {
        let requestModel = values.house

        if (values.rooms.length > 0) {
          let newRooms = []

          values.rooms.forEach((room) => {
            if (!room.removalDate) {
              const beds = values.beds.filter((bed) => bed.roomId === room.id)

              newRooms.push({
                name: room.name,
                bedNames: beds.map((bed) => bed.name),
              })
            }
          })
          requestModel.rooms = newRooms
        }

        if (values.programs.length > 0) {
          requestModel.programs = values.programs.map((program) => {
            return {
              programId: program.program_id,
              organizationId: program.organization_id,
            }
          })
        }

        result = await housingApiCreateHousePost(
          userCtx.tokens.getHousingToken(),
          requestModel,
        )
      }

      if (!!result) {
        try {
          if (fileUploadRef.current.files?.length > 0) {
            const formData = new FormData()
            formData.append("formFile", fileUploadRef.current.files[0])
            formData.append("houseId", result.house.id)

            await housingApiUploadHouseImagePost(
              userCtx.tokens.getHousingToken(),
              formData,
            )
            fileUploadRef.current.clear()
          }

          userCtx.setNotificationInformation("Changes successfully submitted")
          setNewHouseId(undefined)
          onHouseCreated()
        } catch (error) {
          setNewHouseId(result.house.id)
          throw new error(error)
        }
      }
    } catch (error) {
      const errorData = error.response?.data
      const houseNameNotUnique =
        typeof errorData === "string" &&
        errorData.indexOf("The House name is not unique") >= 0

      if (houseNameNotUnique) {
        setErrors({
          name: "The house name is not unique, please enter a unique name",
        })
        userCtx.setNotificationError(
          "Unable to save the house profile: The house name is not unique",
        )
      } else {
        LogError("Unable to save the house profile. ", error)
        userCtx.setNotificationError(
          "Unable to save the house profile. See console for details.",
        )
      }

      return false
    }
  }

  const headerTemplate = (options) => {
    const { className, chooseButton, cancelButton } = options

    return (
      <div
        className={className}
        style={{
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
        }}
      >
        {chooseButton}
        {cancelButton}
      </div>
    )
  }

  const RoomHeader = ({ values, setFieldValue }) => {
    const activeRooms = values.rooms.filter((room) => !room.removalDate).length
    const activeBeds = values.beds.length

    const addNewRoom = () => {
      //Find all new room ids
      const newRoomIds = values.rooms
        .filter((room) => !!room.isNew)
        .map((room) => room.id)

      //Calculate the latest new room iD
      const roomId = newRoomIds.length > 0 ? Math.min(...newRoomIds) - 1 : -1

      let newRoomNumber = values.rooms.length + 1
      if (newRoomNumber < 10) {
        newRoomNumber = `0${newRoomNumber}`
      }

      setFieldValue("rooms", [
        ...values.rooms,
        {
          id: roomId,
          houseId: values.house.id,
          name: `room #${newRoomNumber}`,
          isNew: true,
          removalDate: null,
        },
      ])

      setRefresh(!refresh)
    }

    return (
      <div className={myStyle.roomFormHeaderGrid}>
        <Tooltip target=".houseFormRoomHeader_tt" />
        <div className={myStyle.roomFormGridItemTitle}>Rooms:</div>
        <div
          className={`houseFormRoomHeader_tt ${myStyle.roomFormGridItemRooms}`}
          data-pr-tooltip="Rooms available in this house"
        >
          <span className={`material-icons`}>{"house"}</span>
          {`: ${activeRooms}`}
        </div>
        <div
          className={`houseFormRoomHeader_tt ${myStyle.roomFormGridItemBeds}`}
          data-pr-tooltip="Beds available in this house"
        >
          <span className={`material-icons`}>{"bed"}</span>
          {`: ${activeBeds}`}
        </div>
        <div
          className={`houseFormRoomHeader_tt ${myStyle.roomFormGridItemOccupant}`}
          data-pr-tooltip="Participants allocated to beds in this house"
        >
          <span className={`material-icons`}>{"person"}</span>
          <label>{`: ${occupiedBeds}`}</label>
        </div>
        <div className={myStyle.roomFormGridItemButton}>
          <button
            className="btn"
            type="button"
            onClick={() => {
              addNewRoom()
            }}
          >
            New Room
          </button>
        </div>
      </div>
    )
  }

  const AddNewBed = (room, values, setFieldValue) => {
    let roomNumber =
      values.beds.filter((bed) => bed.roomId === room.id && !bed.removalDate)
        .length + 1

    //Add leading zero to single digit numbers
    if (roomNumber < 10) {
      roomNumber = `0${roomNumber}`
    }

    setFieldValue("beds", [
      ...values.beds,
      { roomId: room.id, name: `Bed #${roomNumber}`, isNew: true },
    ])
  }

  const FrequencyField = ({ values, isSubmitting }) => {
    const [, , frequencyDetailHelper] = useField("house.rentFrequencyDetail")
    const [, , rentValueHelper] = useField("house.rentValue")
    const [, , rentGraceDaysHelper] = useField("house.rentGraceDays")

    const onFrequencyChange = (value) => {
      switch (value) {
        case "Weekly":
          setFrequencyOptions(weeklyOptions())
          break
        case "Monthly":
          setFrequencyOptions(monthlyOptions())
          break
        case noRentDueValue:
          rentValueHelper.setValue(0)
          rentGraceDaysHelper.setValue(0)
          break
        default:
          break
      }

      frequencyDetailHelper.setValue(null)
    }

    return (
      <>
        <FormikDropDown
          name="house.rentFrequency"
          label="Default rent frequency"
          helpText="How often will the rent be due for this house?"
          options={["Daily", "Weekly", "Monthly", noRentDueValue]}
          onChangeCallback={onFrequencyChange}
          disabled={isSubmitting}
        />
        {values.house.rentFrequency === "Weekly" ||
        values.house.rentFrequency === "Monthly" ? (
          <FormikDropDown
            name="house.rentFrequencyDetail"
            label="Rent frequency interval"
            helpText="How often will the rent be due for this house?"
            options={frequencyOptions}
            disabled={isSubmitting}
          />
        ) : null}
      </>
    )
  }

  return (
    <Dialog
      header={!!houseId ? "Manage House" : "Create New House"}
      visible={showForm}
      onHide={() => {
        onHideForm()
        setNewHouseId(undefined)
        setHouse(newHouse)
      }}
      style={{ width: "1100px", height: "900px" }}
    >
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <Formik
          initialValues={house}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values, { setErrors }) => {
            return submitHouseProfile(values, setErrors)
          }}
        >
          {({ isSubmitting, values, dirty, setFieldValue }) => (
            <Form>
              <div className={myStyle.mainContainer}>
                <Tooltip target=".houseForm_tt" />
                <div className={myStyle.houseContainer}>
                  <FormikTextInput
                    name="house.name"
                    label="Name"
                    disabled={isSubmitting}
                  />
                  <FormikMultiSelectInput
                    name="house.organizationIds"
                    label="Organizations"
                    helpText="Specify which organizations within this enterprise has access to the template"
                    options={organizations}
                    optionLabel="organization_name"
                    optionValue={"organization_id"}
                    disabled={isSubmitting || isLoadingPrograms}
                  />
                  <HousePrograms
                    isSubmitting={isSubmitting}
                    organizations={organizations}
                    houseOrganizations={values.house.organizationIds}
                    isLoadingPrograms={isLoadingPrograms}
                    onSetIsLoadingPrograms={setIsLoadingPrograms}
                  />
                  <label>House image</label>

                  <div className={myStyle.imageContainer}>
                    {!!houseId || !!newHouseId ? (
                      <div className={myStyle.imageCurrent}>
                        <img
                          className="ratio-16-9 selectDisable houseForm_tt"
                          data-pr-tooltip="The current image associated with this house."
                          height={100}
                          src={houseImageUrl}
                          alt="HouseImage"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    ) : null}

                    <div className={myStyle.imageNew}>
                      <div style={{ marginBottom: "10px" }}>
                        <small style={{ color: "gray" }}>
                          {"Select an image to associate with this house"}
                        </small>
                      </div>
                      <FileUpload
                        ref={fileUploadRef}
                        accept="image/*"
                        style={{ marginBottom: "10px", width: "100%" }}
                        headerTemplate={headerTemplate}
                        customUpload={true}
                        maxFileSize={1048576}
                        multiple={false}
                        onSelect={(e) => {
                          setFieldValue("image", true)
                        }}
                        onClear={(e) => {
                          setFieldValue("image", false)
                        }}
                        onRemove={(e) => {
                          setFieldValue("image", undefined)
                        }}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <HouseAddressSearch />

                  <FormikTextInput
                    name="house.address"
                    label="Street address"
                    disabled={isSubmitting}
                  />
                  <FormikTextInput
                    name="house.city"
                    label="City"
                    disabled={isSubmitting}
                  />
                  <FormikTextInput
                    name="house.state"
                    label="State"
                    disabled={isSubmitting}
                  />
                  <FormikTextInput
                    name="house.zipCode"
                    label="Zip code"
                    disabled={isSubmitting}
                  />
                  <FrequencyField isSubmitting={isSubmitting} values={values} />

                  {values.house.rentFrequency !== noRentDueValue ? (
                    <>
                      <FormikNumberInput
                        name="house.rentValue"
                        label="Default rent value"
                        helpText={
                          "The default rent value for beds at this property"
                        }
                        disabled={isSubmitting}
                        minValue={0}
                        currencyCode={"USD"}
                      />

                      <FormikNumberInput
                        name="house.rentGraceDays"
                        label="Default rent grace period"
                        helpText={
                          "The default amount of days past the due date when rent becomes overdue"
                        }
                        minFractionDigits={0}
                        disabled={isSubmitting}
                        minValue={0}
                      />
                    </>
                  ) : null}
                </div>
                <div className={myStyle.roomContainer}>
                  <div className={myStyle.topButtonContainer}>
                    <button
                      type="button"
                      className={`btn ${myStyle.topCancelButton}`}
                      onClick={() => {
                        onHideForm()
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`btn ${myStyle.topSubmitButton}`}
                      disabled={isSubmitting || !dirty}
                    >
                      Save
                    </button>
                  </div>
                  <RoomHeader values={values} setFieldValue={setFieldValue} />
                  {values.rooms.map((room, index) => {
                    if (!room.removalDate) {
                      return (
                        <Room
                          index={index}
                          values={values}
                          disabled={isLoading || isSubmitting}
                          refresh={() => {
                            setRefresh(!refresh)
                          }}
                          onNewBedClick={() => {
                            AddNewBed(room, values, setFieldValue)
                            setRefresh(!refresh)
                          }}
                          key={index}
                        />
                      )
                    } else {
                      return null
                    }
                  })}
                </div>
              </div>
              <div style={{ float: "right" }}>
                <button
                  type="submit"
                  className="btn"
                  disabled={isSubmitting || !dirty}
                  style={{ marginBottom: "10px" }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ marginLeft: "10px" }}
                  onClick={() => {
                    onHideForm()
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  )
}
